/**
 * Live button for the transport bar of live streams: a dot and a "LIVE" label.
 * Lit (red, flashing) while playback is at the live edge; grey otherwise, when
 * pressing it seeks to the live edge, and starts playback if it was paused.
 * Supports preview mode with yellow dot, faster flash, and "Preview" label.
 *
 * Media Chrome's live button owns the behaviour (the request events, the aria
 * state, the keyboard); this gives it the look and the preview label.
 */

import { MediaLiveButton } from 'media-chrome';
import { t } from 'media-chrome/utils/i18n.js';
import { MediaUIAttributes } from 'media-chrome/constants.js';

const { MEDIA_PAUSED, MEDIA_STREAM_TYPE, MEDIA_TIME_IS_LIVE } =
  MediaUIAttributes;

/** The state of playing at the live edge, for `:host(...)`. */
const PLAYING_LIVE = `[${MEDIA_TIME_IS_LIVE}]:not([${MEDIA_PAUSED}])`;

class BunnyLiveIndicator extends MediaLiveButton {
  static override get observedAttributes(): string[] {
    return [...super.observedAttributes, MEDIA_STREAM_TYPE, 'preview'];
  }

  static override getSlotTemplateHTML(_attrs: Record<string, string>): string {
    return `
      <style>
        :host {
          gap: 6px;
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: #fff;
        }

        :host(:not([${MEDIA_STREAM_TYPE}="live"])) {
          display: none;
        }

        .dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: #8c8c8c;
        }

        /* Playing at the live edge: nothing to press. */
        :host(${PLAYING_LIVE}) {
          cursor: default;
        }

        :host(${PLAYING_LIVE}) .dot {
          background-color: #e53935;
          animation: pulse 2s ease-in-out infinite;
        }

        :host([preview]${PLAYING_LIVE}) .dot {
          background-color: #ffc107;
          animation: pulse-fast 0.8s ease-in-out infinite;
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.4;
          }
        }

        @keyframes pulse-fast {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.3;
          }
        }

        slot[name="text"] {
          line-height: 1;
        }
      </style>
      <span class="dot"></span>
      <slot name="text">LIVE</slot>
    `;
  }

  override connectedCallback() {
    super.connectedCallback();
    this.#updateLabel();
  }

  override attributeChangedCallback(
    attrName: string,
    oldValue: string | null,
    newValue: string | null,
  ) {
    super.attributeChangedCallback(attrName, oldValue, newValue);
    // The base class writes "live" into the label whenever its state changes.
    this.#updateLabel();
  }

  #updateLabel() {
    const label = this.shadowRoot?.querySelector('slot[name="text"]');
    if (label) {
      const translate = t as (key: string) => string;
      label.textContent = translate(
        this.hasAttribute('preview') ? 'preview' : 'live',
      );
    }
  }
}

if (!globalThis.customElements.get('bunny-live-indicator')) {
  globalThis.customElements.define('bunny-live-indicator', BunnyLiveIndicator);
}

export { BunnyLiveIndicator };
